import { flushWidgetAnalytics, reportWidgetEvent } from './report-widget-event.js';

export type WidgetErrorSource =
  | 'bootstrap'
  | 'render'
  | 'realtime'
  | 'history'
  | 'send'
  | 'upload';

export type ReportWidgetErrorOptions = {
  source: WidgetErrorSource;
  fatal?: boolean;
  componentStack?: string | null;
  /** Needed when bootstrap failed before a widget session was created. */
  slug?: string | null;
  /** Fatal errors flush immediately by default. */
  flush?: boolean;
};

const MAX_MESSAGE_LENGTH = 500;
const MAX_STACK_LENGTH = 4_000;
const DUPLICATE_WINDOW_MS = 60_000;
const REDACTED = '[redacted]';
const lastReportedAt = new Map<string, number>();

/**
 * Remove common secrets and personal data before an error reaches our backend.
 * Do not attach arbitrary objects, request bodies, chat text, or host DOM data.
 */
export function sanitizeWidgetErrorText(
  value: string,
  maxLength = MAX_STACK_LENGTH,
): string {
  return value
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, `Bearer ${REDACTED}`)
    .replace(
      /\b(token|authorization|password|secret|api[_-]?key)\s*[:=]\s*["']?[^"',\s}]+/gi,
      `$1=${REDACTED}`,
    )
    .replace(
      /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
      REDACTED,
    )
    .replace(/([?&](?:token|key|auth|signature)=)[^&#\s]+/gi, `$1${REDACTED}`)
    .slice(0, maxLength);
}

function normalizeError(error: unknown): {
  name: string;
  message: string;
  stack?: string;
} {
  if (error instanceof Error) {
    return {
      name: sanitizeWidgetErrorText(error.name || 'Error', 100),
      message: sanitizeWidgetErrorText(error.message || 'Unknown error', MAX_MESSAGE_LENGTH),
      stack: error.stack
        ? sanitizeWidgetErrorText(error.stack, MAX_STACK_LENGTH)
        : undefined,
    };
  }

  return {
    name: 'Error',
    message: sanitizeWidgetErrorText(String(error), MAX_MESSAGE_LENGTH),
  };
}

/**
 * Report only errors caught inside Teo-owned boundaries/catch blocks.
 * Deliberately does not install global window error handlers, which would
 * capture unrelated failures from the host website.
 */
export function reportWidgetError(
  error: unknown,
  options: ReportWidgetErrorOptions,
): void {
  const normalized = normalizeError(error);
  const fatal = options.fatal ?? false;
  const componentStack = options.componentStack?.trim();
  const fingerprint = `${options.source}:${normalized.name}:${normalized.message}`;
  const now = Date.now();
  const previous = lastReportedAt.get(fingerprint) ?? 0;
  if (!fatal && now - previous < DUPLICATE_WINDOW_MS) return;
  lastReportedAt.set(fingerprint, now);

  reportWidgetEvent(
    'widget_runtime_error',
    {
      source: options.source,
      name: normalized.name,
      message: normalized.message,
      stack: normalized.stack,
      component_stack: componentStack
        ? sanitizeWidgetErrorText(componentStack, MAX_STACK_LENGTH)
        : undefined,
      fatal,
    },
    {
      slug: options.slug,
      backendOnly: true,
    },
  );

  if (options.flush ?? fatal) {
    void flushWidgetAnalytics();
  }
}
